import ServiceCard from '@/components/common/cards/service-card/ServiceCard';
import ErrorMessage from '@/components/common/error-message/ErrorMessage';
import GoBack from '@/components/common/go-back/GoBack';
import AlertModal from '@/components/common/modals/alert-modal/AlertModal';
import NoData from '@/components/common/no-data/NoData';
import GroupDivisions from '@/components/section/service-detail/groupDivisions/GroupDivisions';
import ServiceDivision from '@/components/section/service-detail/serviceDivision/ServiceDivision';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import useGetServiceById from '@/hooks/queries/services-api/useGetServiceById';
import usePatchService from '@/hooks/queries/services-api/usePatchService';
import { cn } from '@/lib/utils';
import { AlertCircle, Calendar, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const ServiceDetailPage = () => {
  const { t } = useTranslation();
  const { id: serviceId } = useParams();
  const { data: service, isError, isLoading } = useGetServiceById(serviceId);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [switchDivisions, setSwitchDivisions] = useState(false);
  const [switchGroups, setSwitchGroups] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});

  const { mutate, isPending } = usePatchService({
    isSwitchService: true
  });

  useEffect(() => {
    if (service?.activeDivisionType === 'default') {
      setSwitchDivisions(true);
    }
    if (service?.activeDivisionType === 'range') {
      setSwitchGroups(true);
    }
  }, [service]);

  if (isLoading) {
    return (
      <>
        <GoBack />
        <Skeleton className="w-full h-[80vh]" />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <GoBack />
        <NoData />
        <ErrorMessage />
      </>
    );
  }

  if (!service) {
    return (
      <>
        <GoBack />
        <NoData />
      </>
    );
  }

  const onSwitchService = () => {
    const status = service.enabled;
    mutate({ payload: { enabled: !status }, serviceId: service.id });
  };

  const onSwitchShowOnlyInLanding = () => {
    const status = service.showOnlyInLanding;
    mutate({ payload: { showOnlyInLanding: !status }, serviceId: service.id });
  };

  const { serviceDivisions } = service;

  const handleStructureToggle = (type: 'divisions' | 'groups') => {
    const isDivisionsActive = switchDivisions;
    const isGroupsActive = switchGroups;
    const disableMessage =
      type === 'divisions'
        ? t('modals.alert.disableDivisions')
        : t('modals.alert.disableGroups');
    const isTargetSwitchActive =
      type === 'divisions' ? isDivisionsActive : isGroupsActive;
    const isOtherSwitchActive =
      type === 'divisions' ? isGroupsActive : isDivisionsActive;

    if (!isDivisionsActive && !isGroupsActive) {
      // Si ambos están apagados, encender el que se ha tocado
      if (type === 'divisions') {
        mutate({
          payload: { activeDivisionType: 'default' },
          serviceId: serviceId ?? ''
        });
        setSwitchDivisions(true);
      } else {
        setSwitchGroups(true);
        mutate({
          payload: { activeDivisionType: 'range' },
          serviceId: serviceId ?? ''
        });
      }
    } else if (isTargetSwitchActive && !isOtherSwitchActive) {
      // Si el switch actual está encendido y el otro apagado, preguntar para apagar
      setModalMessage(disableMessage);
      setPendingAction(() => () => {
        mutate({
          payload: { activeDivisionType: null },
          serviceId: serviceId ?? ''
        });
        if (type === 'divisions') {
          setSwitchDivisions(false);
        } else {
          setSwitchGroups(false);
        }
      });
      setOpenAlertModal(true);
    } else if (!isTargetSwitchActive && isOtherSwitchActive) {
      // Si el switch actual está apagado y el otro encendido, preguntar para cambiar
      setModalMessage(t('modals.alert.toggleSwitch'));
      setPendingAction(() => () => {
        if (type === 'divisions') {
          setSwitchDivisions(true);
          setSwitchGroups(false);
          mutate({
            payload: { activeDivisionType: 'default' },
            serviceId: serviceId ?? ''
          });
        } else {
          setSwitchGroups(true);
          setSwitchDivisions(false);
          mutate({
            payload: { activeDivisionType: 'range' },
            serviceId: serviceId ?? ''
          });
        }
      });
      setOpenAlertModal(true);
    }
  };

  return (
    <div className="mb-4">
      <GoBack />
      <div className="space-y-6">
        <p className="font-bold">{t('serviceDetail.serviceInfo')}</p>
        <ServiceCard
          service={service}
          onSwitchService={onSwitchService}
          isPendingSwitch={isPending}
          disabledSwitch={!serviceDivisions?.length}
          onSwitchShowOnlyInLanding={onSwitchShowOnlyInLanding}
        />
        <AlertModal
          open={openAlertModal}
          onOpenChange={() => setOpenAlertModal(!openAlertModal)}
          title={t('modals.deleteEquipmentType.title')}
          description={modalMessage}
          acceptText={t('common.accept')}
          cancelText={t('common.cancel')}
          onAccept={() => {
            pendingAction();
            setOpenAlertModal(false);
          }}
        />
        {/* Nueva sección de configuración de estructura */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t('serviceDetail.divisionConfiguration.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('serviceDetail.divisionConfiguration.description')}
          </p>
        </div>

        {/* Status actual */}
        <div className="p-4 rounded-md bg-accent">
          <div className="flex items-center mb-2">
            <AlertCircle className="w-4 h-4 mr-2" />
            <p className="font-semibold">
              {t('serviceDetail.divisionConfiguration.activationType')}
            </p>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            {t('serviceDetail.divisionConfiguration.activationDescription')}
          </p>
          <div className="flex items-center">
            <div
              className={cn('w-3 h-3 rounded-full mr-2', {
                'bg-green-500': switchDivisions || switchGroups,
                'bg-gray-400': !switchDivisions && !switchGroups
              })}
            />
            <span className="text-sm font-medium">
              {switchDivisions &&
                t('serviceDetail.divisionConfiguration.divisionsActive')}
              {switchGroups &&
                t('serviceDetail.divisionConfiguration.groupsActive')}
              {!switchDivisions &&
                !switchGroups &&
                t('serviceDetail.divisionConfiguration.noneActive')}
            </span>
          </div>
          {!switchDivisions && !switchGroups && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('serviceDetail.divisionConfiguration.activateToUse')}
            </p>
          )}
        </div>

        {/* Opciones de estructura */}
        <div className="space-y-3">
          {/* Divisiones tradicionales */}
          <div
            className={cn('p-4 rounded-md border transition-all duration-200', {
              'border-green-500': switchDivisions,
              'border-gray-200 bg-white hover:border-gray-300': !switchDivisions
            })}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center">
                <Clock
                  className={cn('w-5 h-5 mr-3', {
                    'text-green-600': switchDivisions,
                    'text-gray-500': !switchDivisions
                  })}
                />
                <div>
                  <h3
                    className={cn('font-medium', {
                      'text-green-700': switchDivisions,
                      'text-gray-700': !switchDivisions
                    })}
                  >
                    {t('serviceDetail.divisionConfiguration.divisions')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'serviceDetail.divisionConfiguration.divisionsDescription'
                    )}
                  </p>
                </div>
              </div>
              <Switch
                checked={switchDivisions}
                onCheckedChange={() => handleStructureToggle('divisions')}
                disabled={isPending}
              />
            </div>
            {switchDivisions && (
              <div className="pt-3 mt-3 border-t border-green-200">
                <p className="text-xs font-medium text-green-600">
                  ✓ {t('serviceDetail.divisionConfiguration.enableDivisions')}
                </p>
              </div>
            )}
          </div>

          {/* Grupos de fechas */}
          <div
            className={cn('p-4 rounded-md border transition-all duration-200', {
              'border-green-500': switchGroups,
              'border-gray-200 bg-white hover:border-gray-300': !switchGroups
            })}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center">
                <Calendar
                  className={cn('w-5 h-5 mr-3', {
                    'text-green-600': switchGroups,
                    'text-gray-500': !switchGroups
                  })}
                />
                <div>
                  <h3
                    className={cn('font-medium', {
                      'text-green-700': switchGroups,
                      'text-gray-700': !switchGroups
                    })}
                  >
                    {t('serviceDetail.divisionConfiguration.groups')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('serviceDetail.divisionConfiguration.groupsDescription')}
                  </p>
                </div>
              </div>
              <Switch
                checked={switchGroups}
                onCheckedChange={() => handleStructureToggle('groups')}
                disabled={isPending}
              />
            </div>
            {switchGroups && (
              <div className="pt-3 mt-3 border-t border-green-200">
                <p className="text-xs font-medium text-green-600">
                  ✓ {t('serviceDetail.divisionConfiguration.enableGroups')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Contenido dinámico basado en la selección */}
        {switchDivisions && (
          <div className="space-y-4">
            {isPending ? (
              <>
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
              </>
            ) : (
              <ServiceDivision
                serviceDivision={serviceDivisions}
                isLoading={isLoading}
                isError={isError}
              />
            )}
          </div>
        )}

        {switchGroups && (
          <div className="space-y-4">
            {isPending ? (
              <>
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
                <Skeleton className="w-full h-6" />
              </>
            ) : (
              <GroupDivisions serviceDivisionGroups={serviceDivisions} service={service} />
            )}
          </div>
        )}

        {!switchDivisions && !switchGroups && (
          <div className="p-8 text-center rounded-md bg-gray-50">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-700">
              {t('serviceDetail.divisionConfiguration.noneActive')}
            </h3>
            <p className="text-gray-500">
              {t('serviceDetail.divisionConfiguration.activateToUse')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDetailPage;
